/**
 * The seam between the mounted artifact iframe and the two hooks that have to talk to it.
 *
 * `stageFor` is a pure function of the entity, called in the host's render body — that is the right shape
 * for it, and it is why `docEditBus` exists in the same folder. The consequence is the same here: the
 * thing that MOUNTS the artifact (`ArtifactDocument`, chosen deep inside `stageFor`) is not reachable
 * from the thing that needs to POST to it (annotate mode, which wants to know what is under a point, and
 * text editing, which drives the bridge). Threading a ref from one to the other means threading it
 * through `stageFor`'s whole switch, which would make a pure function hold a mutable handle.
 *
 * So the frame travels the other way: `ArtifactDocument` registers its window here on load and clears it
 * on unmount, and the hooks read it back through `useSyncExternalStore`. Same module-store shape as
 * `stageSignals` and `docEditBus`, for the same reason.
 *
 * WHY THE WINDOW AND NOT THE ELEMENT. The only thing anyone does with it is `postMessage`, and the only
 * thing anyone does with the replies is filter on `event.source` — both of which are the window.
 * `BirtvyArtifactEditor` states the filter's argument in full: every sandboxed frame reports origin
 * `"null"`, so source-window identity is the ONLY way to tell one artifact's replies from another's.
 * Handing out the element would invite a reader to reach for `contentDocument`, which is null here by
 * design and always will be — the frame is `allow-scripts` with no `allow-same-origin`.
 *
 * UNREGISTERED IS NORMAL, NEVER BROKEN. There is no frame while the pane is empty, while a document is
 * still parsing, or on the server. Every reader has to treat "no frame" as a state rather than a fault:
 * annotate mode still places notes (they carry coordinates, which is what they were always anchored on),
 * and text editing simply does not arm.
 */

/** What a reader gets. `null` whenever no artifact is mounted — which is a state, not a failure. */
export interface ArtifactFrame {
  /** The live window, for `postMessage` out and for `event.source ===` on the way back. */
  win: Window;
  /**
   * Bumped every time a NEW document is mounted in the frame.
   *
   * `ArtifactDocument` remounts the iframe on every source change (a fresh document has to replay its
   * entrance animations), so a reader holding bridge state — an open editor, a pending hit-test, a
   * selected path — is holding it against a document that no longer exists. Element paths are only
   * valid against the exact document they were measured in; this is the number that says "measure
   * again", and it is why the frame is an object rather than a bare window.
   */
  generation: number;
}

let frame: ArtifactFrame | null = null;
const listeners = new Set<() => void>();

function bump(): void {
  for (const listener of listeners) listener();
}

/**
 * Publish the mounted frame. Returns the matching unregister.
 *
 * The unregister clears only ITS OWN registration, the same guard `docEditBus` carries and for the same
 * measured reason: StrictMode mounts run register → unregister → register, and a cleanup that cleared
 * unconditionally would kill the second registration a frame after it landed — leaving a mounted
 * artifact that nothing can talk to and no error anywhere saying so.
 */
export function registerArtifactFrame(win: Window, generation: number): () => void {
  const next: ArtifactFrame = { win, generation };
  frame = next;
  bump();
  return () => {
    if (frame === next) {
      frame = null;
      bump();
    }
  };
}

/** `useSyncExternalStore`'s snapshot. Identity is stable between registrations, so a reader that
 *  subscribes does not re-render until the frame genuinely changes. */
export function readArtifactFrame(): ArtifactFrame | null {
  return frame;
}

/** `useSyncExternalStore`'s subscribe half. */
export function subscribeArtifactFrame(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** The server mounts no iframe and never will, so its snapshot has to be a constant. */
export function serverArtifactFrame(): null {
  return null;
}

/* ── talking to it ─────────────────────────────────────────────────────────────────────────────── */

/**
 * Post to the artifact, if there is one. Silent when there is not, which is the honest behaviour: every
 * message this carries is a request about a document, and with no document there is nothing to refuse.
 *
 * `'*'` as the target origin because the frame's origin IS `"null"` — an opaque origin, deliberately, so
 * that model-authored HTML cannot reach this one. There is no narrower value that would ever match, and
 * the bridge on the other side filters on shape rather than on sender.
 */
export function postToArtifact(message: unknown): boolean {
  const current = frame;
  if (!current) return false;
  try {
    current.win.postMessage(message, '*');
    return true;
  } catch {
    // A window torn down between the read above and the post below. Nothing to report: the caller's
    // next read returns null and it will stand down on its own.
    return false;
  }
}

/* ── writes this app made itself ───────────────────────────────────────────────────────────────── */

/**
 * The one source string the artifact is allowed to adopt WITHOUT remounting.
 *
 * `ArtifactDocument` mounts a fresh iframe on every source change, and it has to: a rewritten document
 * must replay its entrance animations rather than have a new `srcDoc` written into the running one.
 * That is exactly wrong for an edit the document made ITSELF. Typing into a headline commits a new
 * source, the blob comes back changed, the frame remounts — and the caret, the selection and the whole
 * editing session go with it, mid-sentence, on every keystroke that lands.
 *
 * So the editor announces the string it is about to write, and the frame adopts that one string in
 * place. The pixels are already correct: the user watched them change as they typed, and the commit is
 * the app catching up with what the document already shows.
 *
 * NOT CONSUMED on read, deliberately. `ArtifactDocument` performs its generation bump during RENDER,
 * which StrictMode runs twice, and a read that cleared would let the second pass remount anyway. A
 * value left standing can only ever match the identical string again — which would be a no-op render
 * regardless — so leaving it is free.
 */
/**
 * A SET, not one string, because writes overlap.
 *
 * Two style changes inside one round trip put two sources in flight, and they come back in order: the
 * frame is handed the first while the second is already expected. Holding only the latest, the first
 * arrival matches nothing, the frame reads it as a stranger's document and remounts — which is the
 * artifact visibly reloading mid-edit, taking the caret with it. Small cap because the only thing that
 * can be in flight is a write this app made in the last few hundred milliseconds.
 */
const selfWrites = new Set<string>();
const SELF_WRITE_CAP = 4;

/** Announce a source this app is committing on the document's behalf. Call it BEFORE the write. */
export function expectArtifactSelfWrite(html: string): void {
  // Oldest out first. `Set` iterates in insertion order, so this is a queue without being one.
  if (selfWrites.size >= SELF_WRITE_CAP) {
    const oldest = selfWrites.values().next().value;
    if (oldest !== undefined) selfWrites.delete(oldest);
  }
  selfWrites.add(html);
}

/** Is this incoming source one of ours? Read during render; must stay side-effect free. */
export function isArtifactSelfWrite(html: string): boolean {
  return selfWrites.has(html);
}

/**
 * Forget it. Called the moment a DIFFERENT document arrives.
 *
 * Left standing, the expectation outlives the artifact it was about — and then it is not merely
 * stale, it is wrong: open artifact A, edit it, switch to B, switch back to A, and A's incoming
 * source is byte-identical to the string still sitting here. The frame would recognise it as its own
 * write and decline to remount, leaving B's document on screen under A's name.
 */
export function clearArtifactSelfWrite(): void {
  selfWrites.clear();
}

/* ── the hit-test, as a promise ────────────────────────────────────────────────────────────────── */

/**
 * What is under this point in the artifact's own coordinates.
 *
 * The bridge answers `__bds_hit` with `__bds_hitres` carrying the same `seq`, so several hit-tests can
 * be in flight without their answers crossing — which they will be, because annotate mode fires one per
 * animation frame while the pointer moves. Older requests are dropped rather than resolved late: a
 * hover answer that arrives after the pointer has moved on is not a stale answer to keep, it is one to
 * throw away, and resolving it would repaint the highlight somewhere the pointer no longer is.
 *
 * Resolves to `null` when there is no frame, when the point hit nothing, or when the artifact did not
 * answer inside the timeout. A live document always answers — the handler is synchronous and always
 * posts, including the empty answer — so the timeout is for the case the frame went away mid-flight.
 */
export interface ArtifactHit {
  /** Child-index path from body, the bridge's own address for a node. */
  path: number[];
  /** The element's box, in the artifact's own 1920x1080 space. */
  rect: { x: number; y: number; w: number; h: number };
  /** Self, parent, grandparent — as far as the naming rules reach. */
  chain: ArtifactHitDescriptor[];
  /** The nearest `data-eid` at or above the hit (a hand-edited element's name tag), when there is one. */
  eid?: string | null;
}

/** One element's facts. Naming happens in `annotationName`; this is only what it is named FROM. */
export interface ArtifactHitDescriptor {
  tag: string;
  text: string;
  id: string;
  cls: string;
  /** `data-element`, which an artifact may set to name a region itself. It wins over every heuristic. */
  data: string;
  aria: string;
  role: string;
  href: string;
  alt: string;
  /** This element's own `data-eid`, when it has one. */
  eid?: string | null;
}

const HIT_TIMEOUT_MS = 500;

/** Only a well-formed name tag passes: the reply comes from the artifact, and the eid lands in a prompt. */
function eidOf(value: unknown): string | null {
  return typeof value === 'string' && /^e[0-9]{1,6}$/.test(value) ? value : null;
}

let hitSeq = 0;

export function hitTestArtifact(x: number, y: number): Promise<ArtifactHit | null> {
  const current = frame;
  if (typeof window === 'undefined' || !current) return Promise.resolve(null);
  hitSeq += 1;
  const seq = hitSeq;

  return new Promise((resolve) => {
    let done = false;
    const finish = (hit: ArtifactHit | null) => {
      if (done) return;
      done = true;
      window.clearTimeout(timer);
      window.removeEventListener('message', onMessage);
      resolve(hit);
    };

    const onMessage = (event: MessageEvent) => {
      // Source-window identity, for `BirtvyArtifactEditor`'s reason: the page can hold several
      // sandboxed frames (the bake frame is one, and it posts unprompted), they all report origin
      // "null", and acting on a neighbour's reply would name an element in a different document.
      if (event.source !== current.win) return;
      const data = event.data as { __bds_hitres?: unknown; seq?: unknown; path?: unknown; rect?: unknown; chain?: unknown; eid?: unknown };
      if (!data || data.__bds_hitres !== 1 || data.seq !== seq) return;
      if (!Array.isArray(data.path) || !data.rect) {
        // The honest empty answer: the point hit the document's own ground.
        finish(null);
        return;
      }
      finish({
        path: data.path as number[],
        rect: data.rect as ArtifactHit['rect'],
        chain: (Array.isArray(data.chain) ? data.chain : [])
          .filter((d): d is ArtifactHitDescriptor => !!d && typeof d === 'object')
          .map((d) => (d.eid === undefined ? d : { ...d, eid: eidOf(d.eid) })),
        eid: eidOf(data.eid),
      });
    };

    const timer = window.setTimeout(() => finish(null), HIT_TIMEOUT_MS);
    window.addEventListener('message', onMessage);

    if (!postToArtifact({ __bds_hit: { x, y, seq } })) finish(null);
  });
}
